import {
  Button,
  Checkbox,
  SearchBar,
  Select,
  Toast,
  Tooltip,
  type Column,
  type ToastType,
} from "@legal-500/legal500-design-system";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { docParsing, type DocParseResult } from "../../../api/docParsing.service.js";
import type { TaxonomyMode } from "../../../api/submissions.service.js";
import { DataTable } from "../../../components/DataTable/DataTable.js";
import EmptyState from "../../../components/EmptyState/index.js";
import LoadingState from "../../../components/LoadingState/index.js";
import { PaginationControls } from "../../../components/PaginationControls/PaginationControls.js";
import { RetryAllModal, type RetryAllPhase } from "../../../components/Modal/RetryAllModal.js";
import { TaxonomyToggle } from "../../../components/TaxonomyToggle/index.js";
import { useMutation, useQuery } from "../../../hooks/useApi.js";
import { useParsingJobs } from "../../../hooks/useParsingJobs.js";
import { usePermissions } from "../../../hooks/usePermissions.js";
import { TAXONOMY_URL_PARAM, useTaxonomyMode } from "../../../hooks/useTaxonomyMode.js";
import { formatDateTimeShort } from "../../../utils/dateFormatter.js";
import {
  publicationOptionLabel,
  taxonomyFilterParams,
  taxonomyOptionFitsMode,
  taxonomyOptionValue,
  taxonomyUrlParam,
  taxonomyValueFitsMode,
} from "../../../utils/taxonomyFilter.js";
import {
  attentionChipDescription,
  attentionChipLabel,
  attentionChipReasons,
  barTemplateLabel,
  buildBarReviewTooltip,
  isBarReviewTier,
  isRecord,
  readAttentionFromSearchParams,
  readBarTemplate,
  readLogNumber,
} from "./barReview.js";
import styles from "./ParsingJobs.module.css";

// The api client throws Error objects carrying an optional numeric `status`;
// it exports no named type for them, so narrow structurally instead of casting.
function getErrorStatus(err: unknown): number | null {
  if (err instanceof Error && "status" in err && typeof err.status === "number") {
    return err.status;
  }
  return null;
}

// Exported for unit tests: the count columns read a payload the API types as `unknown`, so the
// narrowing is the behaviour worth pinning down.
export function getParsedJsonCounts(parsedJson: unknown): {
  headCount: number | null;
  clientsCount: number | null;
  mattersCount: number | null;
  contactsCount: number | null;
  barristersCount: number | null;
  highlightsCount: number | null;
} {
  const empty = {
    headCount: null,
    clientsCount: null,
    mattersCount: null,
    contactsCount: null,
    barristersCount: null,
    highlightsCount: null,
  };
  if (!isRecord(parsedJson)) {
    return empty;
  }
  const head = isRecord(parsedJson.head) ? parsedJson.head : null;
  const headCount = head
    ? Object.values(head).filter(v => typeof v === "string" && v.length > 0).length
    : null;
  const clientsCount = Array.isArray(parsedJson.clients) ? parsedJson.clients.length : null;
  const mattersCount = Array.isArray(parsedJson.matters) ? parsedJson.matters.length : null;
  const contactsCount = Array.isArray(parsedJson.contacts) ? parsedJson.contacts.length : null;
  // Bar-only: absent on every other template, so both stay null and the cells show an em dash.
  const barristersCount = Array.isArray(parsedJson.barristers)
    ? parsedJson.barristers.length
    : null;
  const highlightsCount = readLogNumber(
    isRecord(parsedJson.log) ? parsedJson.log : null,
    "work_highlights_total",
  );
  return {
    headCount,
    clientsCount,
    mattersCount,
    contactsCount,
    barristersCount,
    highlightsCount,
  };
}

function StatusDot({ status }: { status: DocParseResult["status"] }) {
  const classMap: Record<string, string> = {
    parsed: styles.statusParsed,
    processing: styles.statusProcessing,
    queued: styles.statusQueued,
    failed: styles.statusFailed,
    waiting: styles.statusWaiting,
  };

  const isPulsing = status === "processing" || status === "queued";
  const className = `${styles.statusDot} ${classMap[status] || ""} ${isPulsing ? styles.statusDotPulse : ""}`;

  return <span className={className}>{status.charAt(0).toUpperCase() + status.slice(1)}</span>;
}

function ParsingJobsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toggleVisible, mode, taxonomy, ready } = useTaxonomyMode();
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "");
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "");
  const [editionFilter, setEditionFilter] = useState(searchParams.get("edition") || "");
  const [publicationFilter, setPublicationFilter] = useState(
    searchParams.get(taxonomyUrlParam("publication", mode)) || "",
  );
  const [regionFilter, setRegionFilter] = useState(
    searchParams.get(taxonomyUrlParam("region", mode)) || "",
  );
  const [practiceAreaFilter, setPracticeAreaFilter] = useState(
    searchParams.get(taxonomyUrlParam("practiceArea", mode)) || "",
  );
  const [olderThanFilter, setOlderThanFilter] = useState(
    searchParams.get("olderThanMinutes") || "",
  );
  const [emptyHead, setEmptyHead] = useState(searchParams.get("emptyHead") === "true");
  const [emptyClients, setEmptyClients] = useState(searchParams.get("emptyClients") === "true");
  const [emptyContacts, setEmptyContacts] = useState(searchParams.get("emptyContacts") === "true");
  const [emptyMatters, setEmptyMatters] = useState(searchParams.get("emptyMatters") === "true");
  const [attentionFilter, setAttentionFilter] = useState(
    readAttentionFromSearchParams(searchParams),
  );
  const [barTemplateFilter, setBarTemplateFilter] = useState(searchParams.get("barTemplate") || "");
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get("page")) || 1);
  const [itemsPerPage, setItemsPerPage] = useState(Number(searchParams.get("pageSize")) || 25);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [reparsingId, setReparsingId] = useState<string | null>(null);
  const [retryAllState, setRetryAllState] = useState<{
    phase: RetryAllPhase;
    enqueued: number;
    skipped: number;
    errorMessage: string | null;
    errorStatus: number | null;
  } | null>(null);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);
  const { mutate: parseFile } = useMutation(docParsing.parseFile);
  const { hasPermission } = usePermissions();
  const canReparse = hasPermission("submission.parsing.write");

  const { data: filterOptionsData } = useQuery(
    () => docParsing.getFilterOptions(taxonomy),
    [taxonomy],
  );
  const filterOptions = filterOptionsData?.options;

  const debouncedSearchTerm = searchParams.get("search") || "";

  const offset = (currentPage - 1) * itemsPerPage;

  const taxonomyFilters = {
    ...taxonomyFilterParams("publication", publicationFilter, mode),
    ...taxonomyFilterParams("region", regionFilter, mode),
    ...taxonomyFilterParams("practiceArea", practiceAreaFilter, mode),
    taxonomy,
  };

  const { data, loading, error, refetch } = useParsingJobs({
    status: statusFilter || undefined,
    search: debouncedSearchTerm || undefined,
    limit: itemsPerPage,
    offset,
    edition: editionFilter || undefined,
    ...taxonomyFilters,
    emptyHead: emptyHead || undefined,
    emptyClients: emptyClients || undefined,
    emptyContacts: emptyContacts || undefined,
    emptyMatters: emptyMatters || undefined,
    barTemplate: barTemplateFilter || undefined,
    attention: attentionFilter || undefined,
    olderThanMinutes: olderThanFilter ? Number(olderThanFilter) : undefined,
  });

  const results = data?.results ?? [];
  const total = data?.pagination.total ?? 0;
  const totalPages = Math.ceil(total / itemsPerPage);
  const startIndex = offset;
  const endIndex = offset + results.length;

  const handleReparse = async (row: DocParseResult) => {
    if (!row.sharepointFileId || !row.submissionEntryFileId) return;
    setReparsingId(row.id);
    try {
      await parseFile({
        sharepointFileId: row.sharepointFileId,
        fileName: row.fileName,
        submissionEntryFileId: row.submissionEntryFileId,
      });
      await refetch();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Re-parse failed";
      setToast({ message, type: "error" });
    } finally {
      setReparsingId(null);
    }
  };

  const hasFilters = Boolean(
    statusFilter ||
      debouncedSearchTerm ||
      editionFilter ||
      publicationFilter ||
      regionFilter ||
      practiceAreaFilter ||
      olderThanFilter ||
      emptyHead ||
      emptyClients ||
      emptyContacts ||
      emptyMatters ||
      attentionFilter ||
      barTemplateFilter,
  );

  const handleRetryAllClick = () => {
    if (!hasFilters) return;
    setRetryAllState({
      phase: "confirm",
      enqueued: 0,
      skipped: 0,
      errorMessage: null,
      errorStatus: null,
    });
  };

  const handleRetryAllConfirm = async () => {
    setRetryAllState(prev => (prev ? { ...prev, phase: "progress" } : prev));
    try {
      const result = await docParsing.retryAll({
        status: statusFilter || undefined,
        search: debouncedSearchTerm || undefined,
        edition: editionFilter || undefined,
        ...taxonomyFilters,
        emptyHead: emptyHead || undefined,
        emptyClients: emptyClients || undefined,
        emptyContacts: emptyContacts || undefined,
        emptyMatters: emptyMatters || undefined,
        barTemplate: barTemplateFilter || undefined,
        attention: attentionFilter || undefined,
        olderThanMinutes: olderThanFilter ? Number(olderThanFilter) : undefined,
      });
      setRetryAllState(prev =>
        prev
          ? {
              ...prev,
              phase: "done",
              enqueued: result.enqueuedCount,
              skipped: result.skippedCount,
            }
          : prev,
      );
      await refetch();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Retry all failed";
      setRetryAllState(prev =>
        prev
          ? { ...prev, phase: "error", errorMessage: message, errorStatus: getErrorStatus(err) }
          : prev,
      );
    }
  };

  const columns: Column<DocParseResult>[] = [
    {
      header: "ID",
      accessor: "id",
    },
    {
      header: "File name (SharePoint)",
      accessor: "fileName",
      render: (_value, row) => (
        <span className={styles.fileNameCell} title={row.fileName}>
          {row.fileName}
        </span>
      ),
    },
    {
      header: "Original file name",
      accessor: "originalFileName",
      render: (_value, row) => {
        if (!row.originalFileName) {
          return <span style={{ color: "var(--color-text-secondary)" }}>—</span>;
        }
        return (
          <span className={styles.fileNameCell} title={row.originalFileName}>
            {row.originalFileName}
          </span>
        );
      },
    },
    {
      header: "Firm",
      accessor: "firmName",
      render: (_value, row) => {
        if (!row.firmName) {
          return <span style={{ color: "var(--color-text-secondary)" }}>—</span>;
        }
        return (
          <span className={styles.fileNameCell} title={row.firmName}>
            {row.firmName}
          </span>
        );
      },
    },
    {
      header: "Status",
      accessor: "status",
      render: (_value, row) => <StatusDot status={row.status} />,
    },
    {
      header: "Created",
      accessor: "createdAt",
      render: (_value, row) => formatDateTimeShort(row.createdAt),
    },
    {
      header: "Updated",
      accessor: "updatedAt",
      render: (_value, row) => formatDateTimeShort(row.updatedAt),
    },
    {
      header: "Head",
      accessor: "parsedJson" as keyof DocParseResult,
      render: (_value, row) => {
        const { headCount } = getParsedJsonCounts(row.parsedJson);
        if (headCount === null)
          return <span style={{ color: "var(--color-text-secondary)" }}>—</span>;
        return <>{headCount}</>;
      },
    },
    {
      header: "Clients",
      accessor: "parsedJson" as keyof DocParseResult,
      render: (_value, row) => {
        const { clientsCount } = getParsedJsonCounts(row.parsedJson);
        if (clientsCount === null)
          return <span style={{ color: "var(--color-text-secondary)" }}>—</span>;
        return <>{clientsCount}</>;
      },
    },
    {
      header: "Matters",
      accessor: "parsedJson" as keyof DocParseResult,
      render: (_value, row) => {
        const { mattersCount } = getParsedJsonCounts(row.parsedJson);
        if (mattersCount === null)
          return <span style={{ color: "var(--color-text-secondary)" }}>—</span>;
        return <>{mattersCount}</>;
      },
    },
    {
      header: "Contacts",
      accessor: "parsedJson" as keyof DocParseResult,
      render: (_value, row) => {
        const { contactsCount } = getParsedJsonCounts(row.parsedJson);
        if (contactsCount === null)
          return <span style={{ color: "var(--color-text-secondary)" }}>—</span>;
        return <>{contactsCount}</>;
      },
    },
    {
      // "Bar template", not "Template": a non-Bar row em-dashes because the parser records no
      // identifier for the solicitor templates, not because the document has no template.
      header: "Bar template",
      accessor: "parsedJson" as keyof DocParseResult,
      render: (_value, row) => {
        const template = readBarTemplate(row.parsedJson);
        if (template === null)
          return <span style={{ color: "var(--color-text-secondary)" }}>—</span>;
        return <>{barTemplateLabel(template)}</>;
      },
    },
    {
      header: "Barristers",
      accessor: "parsedJson" as keyof DocParseResult,
      render: (_value, row) => {
        const { barristersCount } = getParsedJsonCounts(row.parsedJson);
        if (barristersCount === null)
          return <span style={{ color: "var(--color-text-secondary)" }}>—</span>;
        return <>{barristersCount}</>;
      },
    },
    {
      header: "Highlights",
      accessor: "parsedJson" as keyof DocParseResult,
      render: (_value, row) => {
        const { highlightsCount } = getParsedJsonCounts(row.parsedJson);
        if (highlightsCount === null)
          return <span style={{ color: "var(--color-text-secondary)" }}>—</span>;
        return <>{highlightsCount}</>;
      },
    },
    {
      header: "Review",
      accessor: "barReviewTier" as keyof DocParseResult,
      render: (_value, row) => {
        // A tier the UI does not recognise gets the same placeholder as no tier at all, so a row
        // stored before the rename shows an empty cell instead of an unstyled chip.
        const hasTier = isBarReviewTier(row.barReviewTier);
        // The reasons the API attached that get their own chip here: the parser's verdict IS the
        // tier badge below and "failed" is already the Status column, so only empty / no_template
        // are drawn — the rows that used to sit in this column as an em dash despite needing a look.
        const reasonChips = attentionChipReasons(row.attentionReasons);
        if (!hasTier && reasonChips.length === 0) {
          return <span style={{ color: "var(--color-text-secondary)" }}>—</span>;
        }
        const isMajor = row.barReviewTier === "major";
        // One word at two intensities: the marker alone carries the level — the two chips are
        // otherwise identical outlined badges, because an outline or a heavier label on only one
        // of them made the column read as passed-versus-failed rather than as one flag. The level is
        // spelled out in `title` because a filled-versus-hollow dot is not an accessible
        // distinction on its own — and the visible word is part of that string, so the accessible
        // name still contains the label.
        return (
          <span className={styles.reviewChips}>
            {hasTier && (
              <Tooltip
                title=""
                content={
                  <span className={styles.reviewTooltipText}>
                    {buildBarReviewTooltip(row.barReviewReasons)}
                  </span>
                }
                position="top"
              >
                <span
                  className={styles.reviewChip}
                  title={`Needs review — ${isMajor ? "major" : "minor"}`}
                >
                  <span
                    className={`${styles.reviewMarker} ${isMajor ? "" : styles.reviewMarkerHollow}`}
                    aria-hidden="true"
                  />
                  Review
                </span>
              </Tooltip>
            )}
            {reasonChips.map(reason => (
              <span
                key={reason}
                className={`${styles.reviewChip} ${styles.reviewChipNeutral}`}
                title={attentionChipDescription(reason)}
              >
                {attentionChipLabel(reason)}
              </span>
            ))}
          </span>
        );
      },
    },
    {
      header: "Error",
      accessor: "errorMessage" as keyof DocParseResult,
      render: (_value, row) => {
        if (row.status !== "failed" || !row.errorMessage) {
          return <span style={{ color: "var(--color-text-secondary)" }}>—</span>;
        }
        return (
          <Tooltip title="" content={row.errorMessage} position="top">
            <span className={styles.errorCell}>{row.errorMessage}</span>
          </Tooltip>
        );
      },
    },
    {
      header: "Action",
      accessor: "id" as keyof DocParseResult,
      render: (_value, row) => {
        if (!row.submissionEntryFileId) return null;
        const isActive = row.status === "processing" || row.status === "queued";
        const isReparsing = reparsingId === row.id;
        const disabled = isActive || isReparsing || !row.sharepointFileId || !canReparse;
        return (
          <Button
            variant="outlined"
            color="primary"
            size="small"
            disabled={disabled}
            title={!canReparse ? "Missing submission.parsing.write permission" : undefined}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              handleReparse(row);
            }}
          >
            {isReparsing ? "Sending..." : isActive ? "Processing..." : "Re-parse"}
          </Button>
        );
      },
    },
  ];

  // Auto-refresh when there are active jobs
  useEffect(() => {
    const hasActiveJobs = results.some(r => r.status === "processing" || r.status === "queued");
    if (!hasActiveJobs) return;
    const interval = setInterval(() => refetch(), 10_000);
    return () => clearInterval(interval);
  }, [results, refetch]);

  function updateURL(updates: Record<string, string>) {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value && value !== "") {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });
    setSearchParams(newParams);
  }

  useEffect(() => {
    if (!ready) return;
    const axes = [
      ["publication", publicationFilter, setPublicationFilter],
      ["region", regionFilter, setRegionFilter],
      ["practiceArea", practiceAreaFilter, setPracticeAreaFilter],
    ] as const;
    const stale = axes.filter(([, value]) => !taxonomyValueFitsMode(value, mode));
    if (stale.length === 0) return;
    for (const [, , setValue] of stale) setValue("");
    updateURL(Object.fromEntries(stale.map(([axis]) => [taxonomyUrlParam(axis, mode), ""])));
  }, [ready, mode, publicationFilter, regionFilter, practiceAreaFilter]);

  function handleTaxonomyChange(next: TaxonomyMode) {
    setPublicationFilter("");
    setRegionFilter("");
    setPracticeAreaFilter("");
    setCurrentPage(1);
    updateURL({
      [TAXONOMY_URL_PARAM]: next,
      publicationId: "",
      regionId: "",
      practiceAreaId: "",
      editionId: "",
      rankingEntityId: "",
      bpaId: "",
      page: "1",
    });
  }

  function handleSearchChange(value: string) {
    setSearchTerm(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setCurrentPage(1);
      updateURL({ search: value, page: "1" });
    }, 300);
  }

  useEffect(() => {
    const pageFromURL = Number(searchParams.get("page")) || 1;
    const pageSizeFromURL = Number(searchParams.get("pageSize")) || 25;
    const editionFromURL = searchParams.get("edition") || "";
    const publicationFromURL = searchParams.get(taxonomyUrlParam("publication", mode)) || "";
    const regionFromURL = searchParams.get(taxonomyUrlParam("region", mode)) || "";
    const practiceAreaFromURL = searchParams.get(taxonomyUrlParam("practiceArea", mode)) || "";
    const olderThanFromURL = searchParams.get("olderThanMinutes") || "";
    const barTemplateFromURL = searchParams.get("barTemplate") || "";
    const attentionFromURL = readAttentionFromSearchParams(searchParams);
    if (pageFromURL !== currentPage) setCurrentPage(pageFromURL);
    if (pageSizeFromURL !== itemsPerPage) setItemsPerPage(pageSizeFromURL);
    if (editionFromURL !== editionFilter) setEditionFilter(editionFromURL);
    if (publicationFromURL !== publicationFilter) setPublicationFilter(publicationFromURL);
    if (regionFromURL !== regionFilter) setRegionFilter(regionFromURL);
    if (practiceAreaFromURL !== practiceAreaFilter) setPracticeAreaFilter(practiceAreaFromURL);
    if (olderThanFromURL !== olderThanFilter) setOlderThanFilter(olderThanFromURL);
    if (barTemplateFromURL !== barTemplateFilter) setBarTemplateFilter(barTemplateFromURL);
    if (attentionFromURL !== attentionFilter) setAttentionFilter(attentionFromURL);
    const emptyHeadFromURL = searchParams.get("emptyHead") === "true";
    const emptyClientsFromURL = searchParams.get("emptyClients") === "true";
    const emptyContactsFromURL = searchParams.get("emptyContacts") === "true";
    const emptyMattersFromURL = searchParams.get("emptyMatters") === "true";
    if (emptyHeadFromURL !== emptyHead) setEmptyHead(emptyHeadFromURL);
    if (emptyClientsFromURL !== emptyClients) setEmptyClients(emptyClientsFromURL);
    if (emptyContactsFromURL !== emptyContacts) setEmptyContacts(emptyContactsFromURL);
    if (emptyMattersFromURL !== emptyMatters) setEmptyMatters(emptyMattersFromURL);
  }, [searchParams, mode]);

  return (
    <>
      <div className={styles.searchSection}>
        <SearchBar
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder="Search by file name or firm name..."
          onClear={() => handleSearchChange("")}
        />
      </div>

      {toggleVisible && <TaxonomyToggle mode={mode} onChange={handleTaxonomyChange} />}

      <div className={styles.filterSection}>
        <Select
          label="Status"
          value={statusFilter}
          onChange={e => {
            const newStatus = e.target.value;
            setStatusFilter(newStatus);
            setCurrentPage(1);
            updateURL({ status: newStatus, page: "1" });
          }}
          options={[
            { value: "", label: "All" },
            { value: "waiting", label: "Waiting" },
            { value: "queued", label: "Queued" },
            { value: "processing", label: "Processing" },
            { value: "parsed", label: "Parsed" },
            { value: "failed", label: "Failed" },
          ]}
          size="small"
        />
        <Select
          label="Edition"
          value={editionFilter}
          onChange={e => {
            setEditionFilter(e.target.value);
            setCurrentPage(1);
            updateURL({ edition: e.target.value, page: "1" });
          }}
          options={[
            { value: "", label: "All" },
            ...(filterOptions?.editions.map(e => ({ value: e, label: e })) ?? []),
          ]}
          size="small"
        />
        <Select
          label="Publication"
          value={publicationFilter}
          onChange={e => {
            setPublicationFilter(e.target.value);
            setCurrentPage(1);
            updateURL({ [taxonomyUrlParam("publication", mode)]: e.target.value, page: "1" });
          }}
          options={[
            { value: "", label: "All" },
            ...(filterOptions?.publications
              .filter(p => taxonomyOptionFitsMode("publication", p, mode))
              .map(p => ({
                value: taxonomyOptionValue("publication", p, mode),
                label: publicationOptionLabel(p),
              })) ?? []),
          ]}
          size="small"
        />
        <Select
          label="Region"
          value={regionFilter}
          onChange={e => {
            setRegionFilter(e.target.value);
            setCurrentPage(1);
            updateURL({ [taxonomyUrlParam("region", mode)]: e.target.value, page: "1" });
          }}
          options={[
            { value: "", label: "All" },
            ...(filterOptions?.regions
              .filter(r => taxonomyOptionFitsMode("region", r, mode))
              .map(r => ({
                value: taxonomyOptionValue("region", r, mode),
                label: r.name,
              })) ?? []),
          ]}
          size="small"
        />
        <Select
          label="Practice Area"
          value={practiceAreaFilter}
          onChange={e => {
            setPracticeAreaFilter(e.target.value);
            setCurrentPage(1);
            updateURL({ [taxonomyUrlParam("practiceArea", mode)]: e.target.value, page: "1" });
          }}
          options={[
            { value: "", label: "All" },
            ...(filterOptions?.practiceAreas
              .filter(pa => taxonomyOptionFitsMode("practiceArea", pa, mode))
              .map(pa => ({
                value: taxonomyOptionValue("practiceArea", pa, mode),
                label: pa.name,
              })) ?? []),
          ]}
          size="small"
        />
        <Select
          label="Not updated"
          value={olderThanFilter}
          onChange={e => {
            setOlderThanFilter(e.target.value);
            setCurrentPage(1);
            updateURL({ olderThanMinutes: e.target.value, page: "1" });
          }}
          options={[
            { value: "", label: "Any time" },
            { value: "60", label: "> 1 hour" },
            { value: "360", label: "> 6 hours" },
            { value: "1440", label: "> 24 hours" },
          ]}
          size="small"
        />
        <Checkbox
          label="Head: 0"
          checked={emptyHead}
          onChange={e => {
            setEmptyHead(e.target.checked);
            setCurrentPage(1);
            updateURL({ emptyHead: e.target.checked ? "true" : "", page: "1" });
          }}
        />
        <Checkbox
          label="Clients: 0"
          checked={emptyClients}
          onChange={e => {
            setEmptyClients(e.target.checked);
            setCurrentPage(1);
            updateURL({ emptyClients: e.target.checked ? "true" : "", page: "1" });
          }}
        />
        <Checkbox
          label="Matters: 0"
          checked={emptyMatters}
          onChange={e => {
            setEmptyMatters(e.target.checked);
            setCurrentPage(1);
            updateURL({ emptyMatters: e.target.checked ? "true" : "", page: "1" });
          }}
        />
        <Checkbox
          label="Contacts: 0"
          checked={emptyContacts}
          onChange={e => {
            setEmptyContacts(e.target.checked);
            setCurrentPage(1);
            updateURL({ emptyContacts: e.target.checked ? "true" : "", page: "1" });
          }}
        />
        <Select
          label="Bar template"
          value={barTemplateFilter}
          onChange={e => {
            setBarTemplateFilter(e.target.value);
            setCurrentPage(1);
            updateURL({ barTemplate: e.target.value, page: "1" });
          }}
          options={[
            { value: "", label: "All" },
            { value: "uk_bar_2027", label: "UK Bar 2027" },
            { value: "bar_family_unknown", label: "Bar (other)" },
            { value: "none", label: "No Bar template" },
          ]}
          size="small"
        />
        <Select
          label="Attention"
          value={attentionFilter}
          onChange={e => {
            setAttentionFilter(e.target.value);
            setCurrentPage(1);
            // Changing the select retires the legacy barReview=true spelling from the URL, so the
            // address bar carries one filter, not two that could disagree.
            updateURL({ attention: e.target.value, barReview: "", page: "1" });
          }}
          options={[
            { value: "", label: "All" },
            { value: "any", label: "Needs attention" },
            { value: "verdict", label: "Review verdict" },
            { value: "empty", label: "Empty output" },
            { value: "no_template", label: "No template (Bar)" },
            { value: "failed", label: "Failed" },
          ]}
          size="small"
        />
        {canReparse && (
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            disabled={retryAllState?.phase === "progress" || !hasFilters}
            title={!hasFilters ? "Apply at least one filter first" : undefined}
            onClick={handleRetryAllClick}
          >
            {`Retry All (${total})`}
          </Button>
        )}
      </div>

      <div className={styles.controlsSection}>
        <div className={styles.rowsPerPageControl}>
          <label htmlFor="parsingJobsRowsPerPage" className={styles.rowsPerPageLabel}>
            Rows per page:
          </label>
          <Select
            id="parsingJobsRowsPerPage"
            value={itemsPerPage}
            onChange={e => {
              const newSize = Number(e.target.value);
              setItemsPerPage(newSize);
              setCurrentPage(1);
              updateURL({ pageSize: String(newSize), page: "1" });
            }}
            options={[
              { value: 10, label: "10" },
              { value: 25, label: "25" },
              { value: 50, label: "50" },
              { value: 100, label: "100" },
            ]}
            size="small"
          />
        </div>
      </div>

      <div className={styles.contentSection}>
        {loading && <LoadingState message="Loading parsing jobs..." />}
        {error && !loading && (
          <EmptyState variant="error" title="Failed to load parsing jobs" message={error.message} />
        )}
        {!loading && !error && (
          <>
            <DataTable<DocParseResult>
              data={results}
              columns={columns}
              onRowClick={row =>
                navigate(`${location.pathname}/jobs/${row.id}`, {
                  state: { fileName: row.fileName },
                })
              }
              rowKey="id"
              emptyMessage="No parsing jobs found matching your criteria."
            />

            {results.length > 0 && (
              <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                startIndex={startIndex}
                endIndex={endIndex}
                totalCount={total}
                itemName="jobs"
                onPageChange={page => {
                  setCurrentPage(page);
                  updateURL({ page: String(page) });
                }}
              />
            )}
          </>
        )}
      </div>

      {retryAllState && (
        <RetryAllModal
          phase={retryAllState.phase}
          total={total}
          enqueued={retryAllState.enqueued}
          skipped={retryAllState.skipped}
          errorMessage={retryAllState.errorMessage}
          errorStatus={retryAllState.errorStatus}
          onConfirm={() => {
            void handleRetryAllConfirm();
          }}
          onClose={() => setRetryAllState(null)}
        />
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
          duration={5000}
        />
      )}
    </>
  );
}

export default ParsingJobsPage;
